/**
 * Authentication provider component
 * Initializes auth state and sets up automatic token refresh
 */

import { useEffect, useRef } from 'react';
import { useAuthStore } from './auth-store';
import { tokenStorage } from './token-storage';

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const initialize = useAuthStore(state => state.initialize);
  const refreshTokens = useAuthStore(state => state.refreshTokens);
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const refreshIntervalRef = useRef<number | null>(null);

  // Initialize auth on mount
  useEffect(() => {
    initialize();
  }, [initialize]);

  // Set up automatic token refresh
  useEffect(() => {
    if (!isAuthenticated) {
      // Clear any existing refresh interval
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      return;
    }

    // Check if we need to refresh every minute
    refreshIntervalRef.current = window.setInterval(() => {
      if (tokenStorage.shouldRefresh()) {
        refreshTokens().catch(error => {
          console.error('Automatic token refresh failed:', error);
        });
      }
    }, 60 * 1000); // Check every minute

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [isAuthenticated, refreshTokens]);

  // Persist tokens before page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isAuthenticated) {
        tokenStorage.persistToSession();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isAuthenticated]);

  return <>{children}</>;
}
